import { useEffect, useId, useRef, useState } from 'react';
import { Check, Monitor, Moon, Sun, ChevronDown } from 'lucide-react';
import { useTheme, type ThemePreference } from '../theme';
import { ThemeWelcome } from './ThemeWelcome';

const options = [
  { value: 'light', title: '浅色', Icon: Sun },
  { value: 'dark', title: '深色', Icon: Moon },
  { value: 'system', title: '跟随系统', Icon: Monitor },
] as const;

export function ThemeToggle({
  label: _label = false,
  mode = 'appearance',
  showHint = false,
}: {
  label?: boolean;
  showHint?: boolean;
  mode?: 'appearance' | 'combined';
}) {
  const { theme, preference, setTheme, pageStyle, setPageStyle } = useTheme();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  useEffect(() => {
    if (!open) return;
    function dismiss(event: PointerEvent) {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    }
    function escape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        trigger.current?.focus();
      }
    }
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);
  function choose(value: ThemePreference) {
    setTheme(value);
  }
  return (
    <div
      className="theme-picker theme-studio"
      ref={root}
      onBlur={(event) => {
        if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node))
          setOpen(false);
      }}
    >
      <button
        ref={trigger}
        type="button"
        className="theme-toggle"
        aria-label="选择主题"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen(!open)}
      >
        {theme === 'dark' ? (
          <Moon size={18} aria-hidden="true" />
        ) : (
          <Sun size={18} aria-hidden="true" />
        )}
        <span>主题</span>
        <ChevronDown size={14} className="theme-trigger-chevron" aria-hidden="true" />
      </button>
      {showHint && <ThemeWelcome menuOpen={open} onOpen={() => setOpen(true)} />}
      {open && (
        <div className="theme-picker-panel" id={panelId} role="group" aria-label="外观主题">
          <h2 className="theme-studio-title">外观设置</h2>
          {mode === 'combined' && (
            <section className="theme-studio-section" aria-label="界面风格">
              <h3>界面风格</h3>
              <div className="theme-style-grid">
                {(
                  [
                    { value: 'classic', title: '经典海岸' },
                    { value: 'island', title: '海岛探索' },
                  ] as const
                ).map(({ value, title }) => (
                  <button
                    type="button"
                    key={value}
                    className="theme-style-card"
                    aria-pressed={pageStyle === value}
                    onClick={() => setPageStyle(value)}
                  >
                    <span className="theme-style-preview">
                      <img src={`/art/theme-previews/${value}.webp`} alt="" />
                      {pageStyle === value && (
                        <span className="theme-selected-check">
                          <Check size={13} />
                        </span>
                      )}
                    </span>
                    <span>{title}</span>
                  </button>
                ))}
              </div>
            </section>
          )}
          <section className="theme-studio-section" aria-label="明暗模式">
            <h3>明暗模式</h3>
            <div className="theme-mode-grid">
              {options.map(({ value, title, Icon }) => (
                <button
                  key={value}
                  type="button"
                  className="theme-mode-card"
                  aria-pressed={preference === value}
                  onClick={() => choose(value)}
                >
                  <Icon size={25} strokeWidth={1.7} aria-hidden="true" />
                  <span>{title}</span>
                  {preference === value && (
                    <span className="theme-selected-check">
                      <Check size={12} />
                    </span>
                  )}
                </button>
              ))}
            </div>
          </section>
          <p className="theme-studio-motto">让小岛，成为你喜欢的样子</p>
        </div>
      )}
    </div>
  );
}
